import {
  Box,
  Button,
  Heading,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import {
  COHORT_MARKOV_METRIC_REVENUE,
  COHORT_MARKOV_METRIC_ROAS,
  cohortMarkovFillApi,
  cohortMarkovFillErrorMessage,
  downloadCohortMarkovSampleApi,
} from 'api/toolingApi';
import ContentBox from 'components/ContentBox';
import OverlaySpinnerWrapper from 'components/OverlaySpinner';
import FileInput from 'components/inputs/FileInput';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

const ALLOWED_FILE_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
];

function CohortMarkovFill() {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState();
  const [metric, setMetric] = useState(COHORT_MARKOV_METRIC_ROAS);

  const onSubmit = async () => {
    if (!file) {
      toast({
        title: 'Select a file',
        description: 'Upload an .xlsx or .csv cohort file first.',
        status: 'warning',
      });
      return;
    }

    setLoading(true);
    try {
      const downloadName = await cohortMarkovFillApi(file, metric);
      toast({
        title: 'Success',
        description: `Downloaded ${downloadName}`,
        status: 'success',
      });
    } catch (error) {
      const description = await cohortMarkovFillErrorMessage(error);
      toast({
        title: 'File could not be processed',
        description,
        status: 'error',
        duration: 12000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadSample = async () => {
    try {
      await downloadCohortMarkovSampleApi(metric);
      toast({
        title: 'Downloaded',
        description: 'Sample template saved.',
        status: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Could not download sample template.',
        status: 'error',
      });
    }
  };

  return (
    <OverlaySpinnerWrapper show={loading}>
      <ContentBox>
        <VStack align="stretch" spacing={6} maxW="640px">
          <Box>
            <Link
              as={RouterLink}
              to="/tooling"
              fontSize="sm"
              color="brand.500"
            >
              Back to Tooling
            </Link>
            <Heading size="md" mt={2}>
              Cohort Markov Fill
            </Heading>
            <Text mt={2} fontSize="sm" color="gray.600">
              Fixed columns: A = Cohort number, B = Cohort date, C = Marketing
              cost, then period values from column D (Roas M0/M1… or Revenue
              M0/M1…). Choose the data type below. Output includes revenue and
              ROAS per period plus a Runoff row.
            </Text>
            <Button mt={3} size="sm" variant="outline" onClick={downloadSample}>
              Download sample template
            </Button>
          </Box>

          <Box>
            <Text fontSize="sm" fontWeight={600} mb={2}>
              Period columns contain
            </Text>
            <RadioGroup value={metric} onChange={setMetric}>
              <Stack direction="row" spacing={6}>
                <Radio value={COHORT_MARKOV_METRIC_ROAS}>ROAS</Radio>
                <Radio value={COHORT_MARKOV_METRIC_REVENUE}>Revenue</Radio>
              </Stack>
            </RadioGroup>
          </Box>

          <Box>
            <Text fontSize="sm" fontWeight={600} mb={2}>
              Cohort file
            </Text>
            <FileInput
              allowedFileTypes={ALLOWED_FILE_TYPES}
              onSelectFiles={(files) => setFile(files?.[0])}
            />
          </Box>

          <Button variant="brand" onClick={onSubmit} alignSelf="flex-start">
            Fill and download
          </Button>
        </VStack>
      </ContentBox>
    </OverlaySpinnerWrapper>
  );
}

export default CohortMarkovFill;
